/**
 * Fatiado de lib/api.ts por domínio (#131, regra R3).
 */
import { get, post, put, patch, del, postForm, BASE, request } from './client.js'
// ─── Categories ───────────────────────────────────────────────────────────────

export type CategoryTranslation = {
  id: string
  category_id: string
  locale: string
  name: string
  translation_status: string
}

export type Category = {
  id: string
  tenant_id: string
  sort_order: number
  is_active: boolean
  created_at: number
  updated_at: number
  translations: CategoryTranslation[]
}

export const categories = {
  list: () => get<Category[]>('/admin/categories'),
  create: (body: { name: string; locale: string; sort_order?: number }) =>
    post<Category>('/admin/categories', body),
  update: (id: string, body: { name?: string; sort_order?: number; is_active?: boolean }) =>
    put<Category>(`/admin/categories/${id}`, body),
  delete: (id: string) => del<{ message: string }>(`/admin/categories/${id}`),
  upsertTranslation: (id: string, locale: string, name: string) =>
    put<CategoryTranslation>(`/admin/categories/${id}/translations`, { locale, name }),
}

// ─── Products ─────────────────────────────────────────────────────────────────

export type ProductTranslation = {
  id: string
  product_id: string
  locale: string
  name: string
  description: string | null
  translation_status: string
}

export type ProductImage = {
  id: string
  product_id: string
  r2_key: string
  alt_text: string | null
  sort_order: number
  is_primary: boolean
  url: string
}

export function getImageUrl(r2Key: string): string {
  return `${BASE}/media/${r2Key}`
}

export type ProductIngredient = {
  id: string
  product_id: string
  name: string
  is_removable: boolean
  sort_order: number
}

export type ProductExtra = {
  id: string
  product_id: string
  name: string
  price_type: 'fixed' | 'percent'
  price_value: number
  is_active: boolean
}

export type ProductVariant = {
  id: string
  product_id: string
  name: string
  price_cents: number
  sort_order: number
  is_available: boolean
}

/**
 * #479: escritas de variante trazem o preço base do produto DEPOIS do
 * recálculo derivado (mínimo das variantes disponíveis com preço) — o campo
 * de preço do editor (só de leitura com variantes) atualiza sem recarregar.
 */
export type VariantWriteResult = { product_price_cents: number }

export type Product = {
  id: string
  tenant_id: string
  category_id: string
  sku: string | null
  price_cents: number
  currency: string
  tax_category: string
  is_available: boolean
  is_active: boolean
  available_dine_in: boolean
  available_takeaway: boolean
  available_delivery: boolean
  sort_order: number
  created_at: number
  updated_at: number
  variant_group_label: string | null
  kitchen_id: string | null
  translations: ProductTranslation[]
  images: ProductImage[]
  ingredients: ProductIngredient[]
  extras: ProductExtra[]
  variants: ProductVariant[]
  /** #325: sugestões da IA e/ou lista confirmada pelo dono */
  allergens?: ProductAllergen[]
  /** #325: o dono confirmou a lista (null = sem informação) */
  allergens_confirmed_at?: number | null
  allergens_confirmed_by?: string | null
  /** #325: ingredientes prováveis da IA, por aceitar ou descartar */
  ingredient_suggestions?: IngredientSuggestion[]
  /** #214: atributos do prato (linha única; null = nunca revisado) */
  attributes?: ProductAttributes | null
  /** #209: prato do dia (janela gravada; null = nunca marcado) */
  daily_special?: DailySpecialView | null
}

/** Prato do dia de um produto (#209) — janela no fuso da casa. */
export type DailySpecialView = {
  mode: 'today' | 'weekly' | 'range'
  date: string | null
  days_of_week: number | null
  start_date: string | null
  end_date: string | null
  is_active: boolean
  updated_at: number
}

export type DailySpecialBody = {
  active: boolean
  mode?: 'today' | 'weekly' | 'range'
  date?: string
  days_of_week?: number
  start_date?: string
  end_date?: string
}

/** Atributos de um produto (#214) — dietas, picância, tags, infantil, casa. */
export type ProductAttributes = {
  diet_tags: string[]
  spice_level: number
  flavor_tags: string[]
  is_kids: boolean
  is_signature: boolean
  status: 'suggested' | 'confirmed'
  source: 'menu' | 'ai' | 'owner'
  confirmed_by: string | null
  confirmed_at: number | null
}

export type ProductAttributesBody = {
  diet_tags?: string[]
  spice_level?: number
  flavor_tags?: string[]
  is_kids?: boolean
  is_signature?: boolean
}

/** Ingrediente provável sugerido pela IA no import (#325): nunca é facto sem o dono. */
export type IngredientSuggestion = {
  id: string
  name: string
  source: 'ai'
  confidence: number | null
}

/** Alergénio 1–14 (Reg. UE 1169/2011, Anexo II) de um produto (#325). */
export type ProductAllergen = {
  allergen: number
  status: 'suggested' | 'confirmed'
  source: 'menu' | 'ai' | 'owner'
  confidence: number | null
}

export const products = {
  list: (categoryId?: string) =>
    get<Product[]>(`/admin/products${categoryId ? `?category_id=${categoryId}` : ''}`),
  get: (id: string) => get<Product>(`/admin/products/${id}`),
  create: (body: {
    category_id: string
    name: string
    price_cents: number
    currency?: string
    tax_category?: string
    description?: string
    sku?: string
    sort_order?: number
    /** #403: código BASE do idioma do texto (nada regional) */
    locale?: string
  }) => post<Product>('/admin/products', body),
  update: (
    id: string,
    body: Partial<{
      category_id: string
      name: string
      /** #387: linha de tradução a gravar (locale da linha lida no editor) */
      locale: string
      price_cents: number
      tax_category: string
      description: string | null
      is_available: boolean
      is_active: boolean
      sort_order: number
      variant_group_label: string | null
      kitchen_id: string | null
      available_dine_in: boolean
      available_takeaway: boolean
      available_delivery: boolean
    }>,
  ) => put<Product>(`/admin/products/${id}`, body),
  delete: (id: string) => del<{ message: string }>(`/admin/products/${id}`),
  uploadImage: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return postForm<ProductImage & { url: string }>(`/admin/products/${id}/images`, form)
  },
  deleteImage: (id: string, imageId: string) =>
    del<{ message: string }>(`/admin/products/${id}/images/${imageId}`),
  setPrimaryImage: (id: string, imageId: string) =>
    patch<ProductImage>(`/admin/products/${id}/images/${imageId}/primary`, {}),
  /** #325: o dono confirma/corrige a lista de alergénios (vazia = nenhum) */
  confirmAllergens: (id: string, allergens: number[]) =>
    put<Product>(`/admin/products/${id}/allergens`, { allergens }),
  /** #214: o dono confirma os atributos (corpo = estado final completo) */
  confirmAttributes: (id: string, body: ProductAttributesBody) =>
    put<Product>(`/admin/products/${id}/attributes`, body),
  /** #215: pede sugestões de atributos à IA para um produto (status suggested) */
  suggestAttributes: (id: string) =>
    post<Product>(`/admin/products/attributes/suggest`, { product_id: id }),
  /** #209: liga/desliga o prato do dia (janela no fuso da casa) */
  setDailySpecial: (id: string, body: DailySpecialBody) =>
    put<Product>(`/admin/products/${id}/daily-special`, body),
  /** #325: aceita um ingrediente provável (vira ingrediente do prato) */
  acceptIngredientSuggestion: (id: string, suggestionId: string) =>
    post<Product>(`/admin/products/${id}/ingredient-suggestions/${suggestionId}/accept`, {}),
  /** #325: descarta um ingrediente provável */
  dismissIngredientSuggestion: (id: string, suggestionId: string) =>
    del<{ message: string }>(`/admin/products/${id}/ingredient-suggestions/${suggestionId}`),
  addIngredient: (id: string, body: { name: string; is_removable: boolean; sort_order: number }) =>
    post<ProductIngredient>(`/admin/products/${id}/ingredients`, body),
  updateIngredient: (id: string, ingredientId: string, body: { is_removable: boolean }) =>
    put<ProductIngredient>(`/admin/products/${id}/ingredients/${ingredientId}`, body),
  deleteIngredient: (id: string, ingredientId: string) =>
    del<{ message: string }>(`/admin/products/${id}/ingredients/${ingredientId}`),
  addExtra: (
    id: string,
    body: { name: string; price_type: 'fixed' | 'percent'; price_value: number },
  ) => post<ProductExtra>(`/admin/products/${id}/extras`, body),
  deleteExtra: (id: string, extraId: string) =>
    del<{ message: string }>(`/admin/products/${id}/extras/${extraId}`),
  addVariant: (
    productId: string,
    body: { name: string; price_cents: number; sort_order?: number },
  ) => post<ProductVariant & VariantWriteResult>(`/admin/products/${productId}/variants`, body),
  updateVariant: (
    productId: string,
    variantId: string,
    body: { name?: string; price_cents?: number; is_available?: boolean },
  ) =>
    put<ProductVariant & VariantWriteResult>(
      `/admin/products/${productId}/variants/${variantId}`,
      body,
    ),
  deleteVariant: (productId: string, variantId: string) =>
    del<{ message: string } & VariantWriteResult>(
      `/admin/products/${productId}/variants/${variantId}`,
    ),
}

// ─── Promoções (#205) ─────────────────────────────────────────────────────────

export type PromotionDiscountType = 'percent' | 'fixed_price'

/** Promoção por item (features/promotions): janelas no fuso do restaurante. */
export type Promotion = {
  id: string
  tenant_id: string
  product_id: string
  variant_id: string | null
  discount_type: PromotionDiscountType
  /** percent: 1–90 · fixed_price: preço FINAL em centavos */
  discount_value: number
  starts_at: number | null
  ends_at: number | null
  /** bitmask: bit 0 = segunda … bit 6 = domingo; null = todos os dias */
  days_of_week: number | null
  start_time: string | null
  end_time: string | null
  is_active: boolean
  label: string | null
  created_at: number
  updated_at: number
  /** dados do produto para a lista (nome no idioma primário) */
  product?: { id: string; name: string | null; price_cents: number; is_active: boolean } | null
}

export type PromotionBody = {
  variant_id?: string | null
  discount_type: PromotionDiscountType
  discount_value: number
  starts_at?: number | null
  ends_at?: number | null
  days_of_week?: number | null
  start_time?: string | null
  end_time?: string | null
  is_active?: boolean
  label?: string | null
}

export const promotions = {
  list: (productId?: string, onlyActive = false) =>
    get<Promotion[]>(
      `/admin/promotions${productId ? `?product_id=${productId}` : ''}${productId && onlyActive ? '&only_active=true' : onlyActive ? '?only_active=true' : ''}`,
    ),
  create: (body: PromotionBody & { product_id: string }) =>
    post<Promotion>('/admin/promotions', body),
  update: (id: string, body: Partial<PromotionBody>) =>
    put<Promotion>(`/admin/promotions/${id}`, body),
  setActive: (id: string, active: boolean) =>
    put<Promotion>(`/admin/promotions/${id}/active`, { active }),
}

// ─── Combos ───────────────────────────────────────────────────────────────────

export type ComboItem = { combo_id: string; product_id: string; quantity: number }

export type Combo = {
  id: string
  name: string
  discount_type: 'fixed' | 'percent'
  discount_value: number
  is_active: boolean
  starts_at: number | null
  ends_at: number | null
  created_at: number
  updated_at: number
  items: ComboItem[]
}

export const combos = {
  /** locale: nome do produto de cada item no idioma do painel */
  list: (locale?: string) =>
    get<Combo[]>(`/admin/combos${locale ? `?locale=${encodeURIComponent(locale)}` : ''}`),
  create: (body: {
    name: string
    discount_type: 'fixed' | 'percent'
    discount_value: number
    items: { product_id: string; quantity: number }[]
  }) => post<Combo>('/admin/combos', body),
  update: (
    id: string,
    body: Partial<{
      name: string
      discount_type: 'fixed' | 'percent'
      discount_value: number
      is_active: boolean
      items: { product_id: string; quantity: number }[]
    }>,
  ) => put<Combo>(`/admin/combos/${id}`, body),
  delete: (id: string) => del<{ message: string }>(`/admin/combos/${id}`),
}

// ─── Tax Rules ────────────────────────────────────────────────────────────────

export type TaxRule = {
  id: string
  tax_category: 'food' | 'beverage' | 'tobacco' | 'other'
  rate_percent: number
  label: string
  is_inclusive: boolean
  is_active: boolean
}

export const taxRules = {
  list: () => get<TaxRule[]>('/admin/tax-rules'),
  create: (body: {
    tax_category: TaxRule['tax_category']
    rate_percent: number
    label: string
    is_inclusive: boolean
  }) => post<TaxRule>('/admin/tax-rules', body),
  update: (
    id: string,
    body: Partial<{ rate_percent: number; label: string; is_active: boolean }>,
  ) => put<TaxRule>(`/admin/tax-rules/${id}`, body),
  delete: (id: string) => del<{ message: string }>(`/admin/tax-rules/${id}`),
}

// ─── Tables ───────────────────────────────────────────────────────────────────

export type Table = {
  id: string
  number: number
  label: string | null
  qr_token: string
  is_active: boolean
  /** #132 (0101): mesa unida em outra — fica inativa apontando para o destino */
  merged_with_table_id?: string | null
  created_at: number
}

/** #132: mesa atendida por um garçom (waiter_table_claims) */
export type TableClaim = {
  table_id: string
  table_number: number
  table_label: string | null
  waiter_user_id: string
  waiter_name: string | null
  claimed_at: number
}

/** #132: resultado da união de mesas */
export type TableMergeResult = {
  from_table_id: string
  from_table_number: number
  to_table_id: string
  to_table_number: number
  moved_order_ids: string[]
  bill_action: 'moved' | 'combined' | 'none'
}

export type TableQr = {
  table_id: string
  table_number: number
  qr_token: string
  qr_url: string
}

export type Kitchen = {
  id: string
  name: string
  sort_order: number
  active: boolean
  product_count: number
}

export const kitchensApi = {
  list: () => get<{ kitchens: Kitchen[]; unassigned_count: number }>('/admin/kitchens'),
  create: (body: { name: string }) => post<{ id: string }>('/admin/kitchens', body),
  update: (id: string, body: { name?: string; sort_order?: number; active?: boolean }) =>
    put<{ id: string }>(`/admin/kitchens/${id}`, body),
  remove: (id: string) => del<{ id: string }>(`/admin/kitchens/${id}`),
  assignCategory: (body: { category_id: string; kitchen_id: string | null }) =>
    post<{ updated: number }>('/admin/kitchens/assign-category', body),
}

/** #293 — token de prévia do menu (15 min, só leitura, só deste tenant):
 * o dono vê o menu e as traduções mesmo com o menu público pausado. */
export const menuPreview = {
  create: () => post<{ token: string; expires_at: number }>('/admin/menu-preview', {}),
}

export const tables = {
  list: () => get<Table[]>('/admin/tables'),
  create: (body: { number: number; label?: string }) => post<Table>('/admin/tables', body),
  generate: (count: number, startFrom = 1) =>
    post<Table[]>('/admin/tables/generate', { count, start_from: startFrom }),
  update: (id: string, body: { label?: string | null; is_active?: boolean }) =>
    put<Table>(`/admin/tables/${id}`, body),
  delete: (id: string) => del<{ message: string }>(`/admin/tables/${id}`),
  getQr: (id: string) => get<TableQr>(`/admin/tables/${id}/qr`),
  regenerateQr: (id: string) => request<TableQr>('POST', `/admin/tables/${id}/qr/regenerate`),
  // #132: mesas atendidas + união de mesas
  listClaims: () => get<TableClaim[]>('/admin/tables/claims'),
  claim: (tableId: string, waiterUserId?: string) =>
    put<TableClaim[]>(
      `/admin/tables/claims/${tableId}`,
      waiterUserId ? { waiter_user_id: waiterUserId } : {},
    ),
  releaseClaim: (tableId: string, waiterUserId?: string) =>
    del<TableClaim[]>(
      `/admin/tables/claims/${tableId}${waiterUserId ? `?waiter_user_id=${waiterUserId}` : ''}`,
    ),
  merge: (id: string, targetTableId: string) =>
    post<TableMergeResult>(`/admin/tables/${id}/merge`, { target_table_id: targetTableId }),
}
